import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  TextField,
  Box,
  Typography,
  InputAdornment,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const DiscountsSelectModal = ({ 
  open, 
  onClose, 
  discountsList = [], 
  existingItems = [],
  onAdd 
}) => {
  const [selected, setSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelected([]);
      setSearchQuery('');
    }
  }, [open]);

  // Filter available items (active only, not already added by description)
  const availableItems = useMemo(() => {
    const existingNames = existingItems.map(item => item.name?.toLowerCase());
    return discountsList.filter(item => 
      item.isActive && 
      !item.deletedAt &&
      !existingNames.includes(item.description?.toLowerCase())
    );
  }, [discountsList, existingItems]);

  // Filter by search
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return availableItems;
    const query = searchQuery.toLowerCase();
    return availableItems.filter(item => 
      item.description?.toLowerCase().includes(query)
    );
  }, [availableItems, searchQuery]);

  const handleToggle = (item) => {
    const currentIndex = selected.findIndex(s => s.id === item.id);
    const newSelected = [...selected];

    if (currentIndex === -1) {
      newSelected.push(item);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelected(newSelected);
  };

  const handleSelectAll = () => {
    if (selected.length === filteredItems.length) {
      setSelected([]);
    } else {
      setSelected([...filteredItems]);
    }
  };

  const handleAdd = () => {
    if (selected.length > 0) {
      onAdd(selected);
      onClose();
    }
  };

  const totalAmount = selected.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Select Discounts from Catalog</Typography>
          {selected.length > 0 && (
            <Chip 
              label={`${selected.length} selected • $${totalAmount.toFixed(2)}`}
              color="success"
              size="small"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search discounts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Select All */}
        {filteredItems.length > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Button 
                size="small" 
                onClick={handleSelectAll}
              >
                {selected.length === filteredItems.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {filteredItems.length} discounts available
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
          </>
        )}

        {/* List */}
        {filteredItems.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {availableItems.length === 0 
                ? 'All discounts have been added already'
                : 'No discounts match your search'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: isMobile ? 'unset' : 400, overflow: 'auto' }}>
            {filteredItems.map((item) => {
              const isSelected = selected.some(s => s.id === item.id);
              return (
                <ListItem 
                  key={item.id} 
                  disablePadding
                  secondaryAction={
                    <Box sx={{ textAlign: 'right', mr: 1 }}>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                        ${Number(item.amount || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.defaultExpirationDays} days
                      </Typography>
                    </Box>
                  }
                >
                  <ListItemButton onClick={() => handleToggle(item)} dense>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.description}
                      primaryTypographyProps={{
                        sx: { 
                          pr: 8,
                          fontSize: isMobile ? '0.875rem' : '1rem'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleAdd}
          disabled={selected.length === 0}
          color="success"
        >
          Add {selected.length > 0 ? `(${selected.length})` : ''} Discounts
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscountsSelectModal;
